import React from "react";
import { Container, Typography, Box, Paper, IconButton, Tooltip, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const PrivacyPolicy = () => {
  return (
    <>
      <Tooltip title="Go back to login page">
        <IconButton
          href="/"
          sx={{
            position: "fixed",
            top: 24,
            left: 24,
            zIndex: 1000,
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(30, 58, 138, 0.2)',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.3s ease'
          }}
          color="primary"
          aria-label="Go back to login page"
        >
          <ArrowBackIcon sx={{ fontSize: 30, color: '#1e3a8a' }} />
        </IconButton>
      </Tooltip>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 32px rgba(30, 58, 138, 0.1)'
          }}
        >
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#1e3a8a',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px'
              }}
            >
              Privacy Policy for FlashCard App
            </Typography>

            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                color: '#6b7280',
                fontWeight: 500,
                letterSpacing: '0.2px'
              }}
            >
              Last updated: September 09, 2025
            </Typography>

            <Divider sx={{
              my: 3,
              borderColor: 'rgba(30, 58, 138, 0.2)',
              width: '60%',
              mx: 'auto'
            }} />
          </Box>

          {/* Content Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              This Privacy Policy describes Our policies and procedures on the
              collection, use, and disclosure of Your information when You use the
              FlashCard App (the "Service") and tells You about Your privacy
              rights.
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              We use Your Personal data to provide and improve the Service. By
              using the Service, You agree to the collection and use of
              information in accordance with this Privacy Policy.
            </Typography>

            <Typography variant="h4" gutterBottom sx={{
              mt: 4,
              color: '#1e3a8a',
              fontWeight: 600,
              borderLeft: '4px solid #1e8bafff',
              pl: 2
            }}>
              Information We Collect
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              We collect several different types of information for various
              purposes to provide and improve our Service to you.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{
              mt: 3,
              color: '#1e3a8a',
              fontWeight: 600
            }}>
              1. Personally Identifiable Information
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              While using our Service, we may ask you to provide us with certain
              personally identifiable information that can be used to contact or
              identify you ("Personal Data"). This includes:
            </Typography>

            <Box component="ul" sx={{
              pl: 3,
              '& li': {
                mb: 1,
                color: '#374151',
                lineHeight: 1.6
              }
            }}>
              <li><b>Account Information:</b> Full Name, Email Address, Password</li>
              <li><b>User Profile Information:</b> Admission Number, Date of Birth, Gender</li>
              <li><b>Contact & Address:</b> Permanent/Current Address, Alternate Email</li>
              <li><b>Profile Photo:</b> Access to your device's Photo Library</li>
            </Box>

            <Typography variant="h5" gutterBottom sx={{
              mt: 3,
              color: '#1e3a8a',
              fontWeight: 600
            }}>
              2. Usage and Performance Data
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              We collect information such as quiz answers, scores, attempts,
              pass/fail results, and flashcard practice feedback.
            </Typography>

            <Typography variant="h4" gutterBottom sx={{
              mt: 4,
              color: '#1e3a8a',
              fontWeight: 600,
              borderLeft: '4px solid #1e8bafff',
              pl: 2
            }}>
              How We Use Your Information
            </Typography>

            <Box component="ul" sx={{
              pl: 3,
              '& li': {
                mb: 1,
                color: '#374151',
                lineHeight: 1.6
              }
            }}>
              <li>To provide and maintain our Service.</li>
              <li>To track your learning progress.</li>
              <li>To enable "Remember Me" (credentials stored locally).</li>
              <li>To analyze usage data and improve user experience.</li>
            </Box>

            <Typography variant="h4" gutterBottom sx={{
              mt: 4,
              color: '#1e3a8a',
              fontWeight: 600,
              borderLeft: '4px solid #1e8bafff',
              pl: 2
            }}>
              Data Sharing and Storage
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              We do not sell or trade your information. Your data is stored on our
              servers hosted at <b>http://warals1.ddns.net:9017</b>.
            </Typography>

            <Typography variant="h4" gutterBottom sx={{
              mt: 4,
              color: '#1e3a8a',
              fontWeight: 600,
              borderLeft: '4px solid #1e8bafff',
              pl: 2
            }}>
              Your Rights
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              You have the right to access and edit your profile information
              anytime within the app.
            </Typography>

            <Typography variant="h4" gutterBottom sx={{
              mt: 4,
              color: '#1e3a8a',
              fontWeight: 600,
              borderLeft: '4px solid #1e8bafff',
              pl: 2
            }}>
              Contact Us
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
              If you have any questions, please contact us:
              <br />
              <b>Warals Technology</b> <br />
              Email: info@warals.com <br />
              Phone: 9871115060
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
